import {
  AlertTriangle,
  Bell,
  CalendarDays,
  CheckCircle2,
  Info,
  User,
  type LucideIcon,
} from "lucide-react";
import type { NotificationRow, NotificationSeverity } from "@/api/notifications";

/**
 * D-043 S2 — presentation metadata for notification rows.
 *
 * Severity → DS semantic tokens (D-044g, TZ §6.6):
 * critical=destructive(-soft) · warning=warning · info=info · reminder=muted.
 */

export interface SeverityMeta {
  label: string;
  Icon: LucideIcon;
  /** 30px icon tile: soft bg + strong fg. */
  tileClass: string;
}

export const SEVERITY_META: Record<NotificationSeverity, SeverityMeta> = {
  critical: {
    label: "Критичні",
    Icon: AlertTriangle,
    tileClass: "bg-destructive-soft text-destructive",
  },
  warning: {
    label: "Попередження",
    Icon: Info,
    tileClass: "bg-warning text-warning-foreground",
  },
  info: {
    label: "Інформаційні",
    Icon: CheckCircle2,
    tileClass: "bg-info text-info-foreground",
  },
  reminder: {
    label: "Нагадування",
    Icon: CalendarDays,
    tileClass: "bg-muted text-muted-foreground",
  },
};

/** Legacy stock rows (cCEventType=null) render with a neutral user tile. */
export const LEGACY_TILE = {
  Icon: User,
  tileClass: "bg-muted text-muted-foreground",
} as const;

export const FALLBACK_TILE = {
  Icon: Bell,
  tileClass: "bg-muted text-muted-foreground",
} as const;

/** workSurface badge (per-recipient, AC-017). */
export const WORK_SURFACE_META: Record<string, { label: string; className: string }> = {
  personal_worklist: {
    label: "Мій список",
    className: "bg-success text-success-foreground",
  },
  supervisor_control: {
    label: "Контроль керівника",
    className: "bg-warning text-warning-foreground",
  },
  department_inbox: {
    label: "Вхідні відділу",
    className: "bg-info text-info-foreground",
  },
  admin_health: {
    label: "Стан системи",
    className: "bg-muted text-muted-foreground",
  },
  // D-149 N0 §1.13 — поверхня queue_admin: черга ручного призначення рекламацій
  // (/complaints?scope=manual_queue). Окрема від admin_health свідомо: це РОБОЧА
  // черга з конкретною дією «призначити», а не індикатор стану системи.
  complaint_queue: {
    label: "Черга призначення",
    className: "bg-warning text-warning-foreground",
  },
  relationship: {
    label: "Нагадування",
    className: "bg-muted text-muted-foreground",
  },
};

/** sourceModule → UA pill label (registry sourceModule values). */
export const SOURCE_LABELS: Record<string, string> = {
  task: "Задачі",
  internal_request: "Запити",
  contracts: "Договори",
  meetings: "Зустрічі",
  payments: "Оплати",
  profile_review: "Профіль",
  relationship: "Відносини",
  data_quality: "Якість даних",
  system_health: "Адмін",
  onboarding: "Адмін",
  digest: "Зведення",
  // D-149 N0-L: реєстрове sourceModule complaint-подій було відсутнє в мапі —
  // рядок рендерився без pill-лейбла джерела.
  complaint: "Рекламації",
};

/** Filter chips for the Center (Джерело) — curated subset, mockup order. */
export const SOURCE_FILTERS: { value: string; label: string }[] = [
  { value: "task", label: "Задачі" },
  { value: "internal_request", label: "Запити" },
  { value: "contracts", label: "Договори" },
  { value: "profile_review", label: "Профіль" },
  { value: "meetings", label: "Зустрічі" },
  { value: "complaint", label: "Рекламації" },
  { value: "data_quality", label: "Якість даних" },
];

/** Preferences group labels (groupKey from registry preferenceGroup). */
export const PREFERENCE_GROUP_META: Record<string, { title: string; sub: string }> = {
  completed: {
    title: "Виконані запити та задачі",
    sub: "виконана задача · закритий запит · погоджений договір · звіт зустрічі",
  },
  dates: {
    title: "Нагадування про дати",
    sub: "зустріч сьогодні · оплата сьогодні · договір спливає · follow-up",
  },
  birthdays: {
    title: "Дні народження",
    sub: "контакти та клієнти · блок «Відносини»",
  },
  data_quality: {
    title: "Якість даних",
    sub: "неповний профіль · можливий дубль · підтвердження ІПН/ЄДРПОУ",
  },
};

/**
 * Registry deepLinkPatterns are SPA paths, but two of them point at routes
 * the app serves via query-param drawers. Map them; unknown patterns → null
 * (CTA hidden — no dead links).
 */
export function resolveDeepLink(
  raw: string | null,
  meta?: { sourceEntityType?: string | null; sourceEntityId?: string | null },
): string | null {
  if (!raw || !raw.startsWith("/")) return null;

  const taskMatch = raw.match(/^\/tasks\/([A-Za-z0-9]+)$/);
  if (taskMatch) return `/tasks?open=${taskMatch[1]}`;

  const requestMatch = raw.match(/^\/requests\/([A-Za-z0-9]+)$/);
  if (requestMatch) return `/requests?open=${requestMatch[1]}`;

  if (raw === "/admin/sync") return "/sync-center";

  // D-137b W3 (FE belt) — legacy meeting-source notifications carry a frozen
  // /clients/{accountId} deepLink (pre-W5a emit / meeting_starts_soon reminder),
  // which is a 404 type-mismatch: an account-id in the /clients/:holdingId route
  // (root-cause d137_notif_link_step0). When the row's source IS the
  // CClientContactInteraction, rewrite to the meetings read-drawer using the CCI-id
  // from the row metadata (sourceEntityId, served by the feed DTO Q6) — NOT the
  // account-id embedded in the path. Belt over the prod backfill (W1), not a
  // replacement. No-false-rewrite: genuine client notifications
  // (sourceEntityType=Account/CClientHolding) fall through to /clients unchanged.
  if (
    meta?.sourceEntityType === "CClientContactInteraction" &&
    meta.sourceEntityId &&
    /^\/clients\/[A-Za-z0-9]+/.test(raw)
  ) {
    return `/meetings?open=${meta.sourceEntityId}`;
  }

  // D-137 W5a: meeting deep-links — /meetings?open={id} (read drawer, supervisor
  // read-rescue) and /meetings?report={id} (manager fill wizard) — are query-param
  // routes the MeetingsQueuePage handles; pass them through (were dead links).
  //
  // 🔴 D-149 N0-L (§1.15): /complaints був ВІДСУТНІЙ у цьому списку, тому кожен
  // complaint-deepLink повертав null і CTA ховалась — усі 16 complaint-подій
  // реєстру були неклікабельні, попри коректний, «заморожений» шлях у рядку.
  // Маршрут підтверджено у самому роутері, а не regex-ом: App.tsx має
  // <Route path="/complaints/:id" element={<ComplaintsListPage />} /> (детальний
  // drawer) і <Route path="/complaints"> зі scope-фільтром із query-параметра.
  const passthrough = [/^\/clients\//, /^\/contacts\//, /^\/contracts\//, /^\/admin\//, /^\/meetings/, /^\/complaints/];
  if (passthrough.some((re) => re.test(raw))) return raw;

  if (raw === "/today" || raw === "/notifications") return raw;

  return null;
}

/** Compact relative time: «5 хв», «3 год», «вчора», else dd.mm. */
export function relativeTime(iso: string): string {
  // Espo datetimes are UTC without zone suffix.
  const ts = Date.parse(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(ts)) return "";

  const diffMin = Math.max(0, Math.floor((Date.now() - ts) / 60_000));
  if (diffMin < 1) return "щойно";
  if (diffMin < 60) return `${diffMin} хв`;

  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} год`;
  if (diffH < 48) return "вчора";

  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function rowTile(row: NotificationRow) {
  if (row.kind === "legacy") return LEGACY_TILE;
  if (row.severity && SEVERITY_META[row.severity]) {
    const { Icon, tileClass } = SEVERITY_META[row.severity];
    return { Icon, tileClass };
  }
  return FALLBACK_TILE;
}
